import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { PersonPickerBar } from '@/components/performance/bonificacao/PersonPickerBar'
import { ThemeGrid } from '@/components/performance/bonificacao/ThemeGrid'
import { ThemeRowAccordion, computeThemeKPIs } from '@/components/performance/bonificacao/ThemeRowAccordion'
import { RadarChartViz } from '@/components/performance/radar/RadarChartViz'
import { useBonificacaoStore } from '@/stores/bonificacao-store'
import { useTenant } from '@/lib/tenant/tenant-provider'
import { useAuth } from '@/hooks/useAuth'
import { getEvaluationCategories, getEmployeeEvaluations, getEvaluationScores, listEvaluationItems } from '@/api/performance'
import { getEvaluationMatrix } from '@/api/radar'
import { SkeletonCard } from '@/components/common/SkeletonCard'
import { ChartSkeleton } from '@/components/charts/ChartSkeleton'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/Badge'
import { AlertCircle, BarChart3, CheckCircle, Eye, Info, ListChecks, RotateCcw } from 'lucide-react'
import type { EvaluationCategory, EvaluationItem, EvaluationScore } from '@/types/performance'
import type { RadarChartPoint, EvaluationMatrixResult } from '@/types/radar'

interface CompetenciasSearch {
  employee?: string
  tab?: string
}

type TabId = 'visao-geral' | 'avaliacao-desempenho'

const ADMIN_ROLES = ['admin', 'rh', 'analista-rh', 'vice-presidente']
const DIRECTOR_ROLES = ['diretor-tecnico', 'diretor-executivo']

function CompetenciasPage() {
  const { employee, tab: initialTab } = Route.useSearch()
  const { tenantDomain } = useTenant()
  const { user } = useAuth()
  const selectedEmployeeId = useBonificacaoStore((s) => s.selectedEmployeeId)
  const setSelectedEmployee = useBonificacaoStore((s) => s.setSelectedEmployee)

  const [activeTab, setActiveTab] = useState<TabId>((initialTab as TabId) || 'visao-geral')
  const [categories, setCategories] = useState<EvaluationCategory[]>([])
  const [items, setItems] = useState<EvaluationItem[]>([])
  const [scores, setScores] = useState<EvaluationScore[]>([])
  const [radarData, setRadarData] = useState<{ points: RadarChartPoint[]; individualAverage: number; teamAverage: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (employee) {
      setSelectedEmployee(employee)
    }
  }, [])

  // Determine user role
  const isAdminOrRH = useMemo(() => {
    const role = user?.role
    return role && ADMIN_ROLES.includes(role)
  }, [user])

  const isDirector = useMemo(() => {
    const role = user?.role
    return role && DIRECTOR_ROLES.includes(role)
  }, [user])

  // Directors see only their own data — force employeeId
  useEffect(() => {
    if (isDirector && user?.employee?.employeeId && !isAdminOrRH) {
      if (selectedEmployeeId !== user.employee.employeeId) {
        setSelectedEmployee(user.employee.employeeId)
      }
    }
  }, [isDirector, isAdminOrRH, user?.employee?.employeeId])

  // Read-only: directors always read-only
  const readOnly = useMemo(() => {
    if (isAdminOrRH) return false
    return true
  }, [isAdminOrRH])

  // Whether to show PersonPickerBar (hidden for directors)
  const showPersonPicker = useMemo(() => {
    if (isDirector && !isAdminOrRH) return false
    return true
  }, [isDirector, isAdminOrRH])

  // Fetch data when employee changes
  useEffect(() => {
    if (!selectedEmployeeId || !tenantDomain) {
      setLoading(false)
      return
    }

    let cancelled = false

    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        // Fetch categories, items, evaluations, and radar data in parallel
        const [catRes, itemsRes, evalsRes, matrixRes] = await Promise.all([
          getEvaluationCategories(tenantDomain),
          listEvaluationItems(tenantDomain),
          getEmployeeEvaluations(tenantDomain, selectedEmployeeId, { status: 'all' }),
          getEvaluationMatrix(tenantDomain, selectedEmployeeId).catch(() => null),
        ])

        if (cancelled) return

        const cats = catRes.items.sort((a, b) => a.order - b.order)
        setCategories(cats)
        setItems(itemsRes.items)

        // Get scores from all evaluations for this employee
        const allScores: EvaluationScore[] = []
        for (const evaluation of evalsRes.items) {
          if (evaluation.scores?.length) {
            allScores.push(...evaluation.scores)
          } else {
            try {
              const scoresRes = await getEvaluationScores(tenantDomain, evaluation.evaluationId)
              allScores.push(...scoresRes.items)
            } catch {
              // Some evaluations may not have scores yet
            }
          }
        }

        if (cancelled) return
        setScores(allScores)

        // Set radar chart data from evaluation matrix
        if (matrixRes && 'radarChart' in matrixRes) {
          const matrix = matrixRes as EvaluationMatrixResult
          setRadarData({
            points: matrix.radarChart.individual,
            individualAverage: matrix.radarChart.individualAverage,
            teamAverage: matrix.radarChart.teamAverage,
          })
        } else {
          setRadarData(null)
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'Erro ao carregar dados de avaliação')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchData()
    return () => { cancelled = true }
  }, [selectedEmployeeId, tenantDomain])

  // KPI computation
  const kpis = useMemo(() => {
    if (!categories.length || !items.length) {
      return { mediaGeral: 0, totalItens: 0, validados: 0, requerAcao: 0 }
    }
    return computeThemeKPIs(categories, scores, items)
  }, [categories, scores, items])

  // Check if we have any evaluation data
  const hasEvaluationData = scores.length > 0 || (radarData && radarData.points.length > 0)

  // Scroll to theme and switch tab
  const handleThemeClick = useCallback((themeId: string) => {
    setActiveTab('avaliacao-desempenho')
    setTimeout(() => {
      document.getElementById(themeId)?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }, [])

  // Get items for a specific theme
  const getThemeItems = useCallback(
    (categoryId: string) => items.filter((i) => i.categoryId === categoryId),
    [items]
  )

  // Get scores for a specific theme's items
  const getThemeScores = useCallback(
    (categoryId: string) => {
      const themeItemIds = new Set(items.filter((i) => i.categoryId === categoryId).map((i) => i.itemId))
      return scores.filter((s) => themeItemIds.has(s.itemId))
    },
    [items, scores]
  )

  const employeeInfo = useBonificacaoStore((s) => s.selectedEmployeeInfo)

  const handleRetry = () => {
    setError(null)
    setLoading(true)
    // Trigger re-fetch by toggling employee
    const id = selectedEmployeeId
    setSelectedEmployee(null)
    setTimeout(() => setSelectedEmployee(id), 0)
  }

  return (
    <div>
      {showPersonPicker && <PersonPickerBar />}
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Desempenho por Competências</h1>
          {employeeInfo && (
            <p className="text-brand-gray mt-1">{employeeInfo.name}{employeeInfo.department ? ` — ${employeeInfo.department}` : ''}</p>
          )}
        </div>

        {!selectedEmployeeId && (
          <div className="text-center py-12 text-brand-gray">
            Selecione um colaborador para visualizar as competências
          </div>
        )}

        {/* Loading States */}
        {selectedEmployeeId && loading && (
          <div className="space-y-6">
            {/* KPI Skeleton */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} variant="text" className="h-20 bg-brand-dark-lighter" />
              ))}
            </div>
            {/* Theme Grid Skeleton */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
              <SkeletonCard count={10} />
            </div>
            {/* Radar Chart Skeleton */}
            <div data-testid="radar-chart-section">
              <ChartSkeleton height={300} />
            </div>
          </div>
        )}

        {/* Error State */}
        {selectedEmployeeId && error && (
          <div className="card p-8 text-center">
            <AlertCircle className="w-8 h-8 text-error mx-auto mb-2" />
            <p className="text-error mb-4">{error}</p>
            <button
              type="button"
              onClick={handleRetry}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Tentar novamente
            </button>
          </div>
        )}

        {/* Main Content */}
        {selectedEmployeeId && !loading && !error && (
          <>
            {/* Empty State */}
            {!hasEvaluationData && (
              <div className="card p-8 text-center" data-testid="empty-evaluations">
                <Info className="w-8 h-8 text-brand-gray mx-auto mb-2" />
                <p className="text-brand-gray">Nenhuma avaliação encontrada para este colaborador</p>
              </div>
            )}

            {/* Content with data */}
            {hasEvaluationData && (
              <>
                {/* KPI Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-summary">
                  <div className="metric-card p-4 rounded-lg" data-testid="kpi-media-geral">
                    <div className="flex items-center gap-2 mb-1">
                      <BarChart3 className="w-4 h-4 text-brand-primary" />
                      <span className="metric-label">Média Geral</span>
                    </div>
                    <span className="metric-value">
                      {kpis.mediaGeral > 0 ? kpis.mediaGeral.toFixed(1) : '—'}
                    </span>
                  </div>
                  <div className="metric-card p-4 rounded-lg" data-testid="kpi-total-itens">
                    <div className="flex items-center gap-2 mb-1">
                      <ListChecks className="w-4 h-4 text-brand-gray" />
                      <span className="metric-label">Total Itens</span>
                    </div>
                    <span className="metric-value">{kpis.totalItens}</span>
                  </div>
                  <div className="metric-card p-4 rounded-lg" data-testid="kpi-validados">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="metric-label">Validados</span>
                    </div>
                    <span className="text-2xl font-bold text-green-400">{kpis.validados}</span>
                  </div>
                  <div className="metric-card p-4 rounded-lg" data-testid="kpi-requer-acao">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className="w-4 h-4 text-warning" />
                      <span className="metric-label">Requer Ação</span>
                    </div>
                    <span className={`text-2xl font-bold ${kpis.requerAcao > 0 ? 'text-brand-red' : 'text-white'}`}>
                      {kpis.requerAcao}
                    </span>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 border-b border-brand-gray/10">
                  <button
                    type="button"
                    onClick={() => setActiveTab('visao-geral')}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all duration-200 ${
                      activeTab === 'visao-geral'
                        ? 'border-brand-primary text-brand-primary'
                        : 'border-transparent text-brand-gray hover:text-white hover:border-brand-gray/30'
                    }`}
                    data-testid="tab-visao-geral"
                  >
                    <Eye className="w-4 h-4 inline mr-1.5" />
                    Visão Geral
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('avaliacao-desempenho')}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all duration-200 ${
                      activeTab === 'avaliacao-desempenho'
                        ? 'border-brand-primary text-brand-primary'
                        : 'border-transparent text-brand-gray hover:text-white hover:border-brand-gray/30'
                    }`}
                    data-testid="tab-avaliacao"
                  >
                    <ListChecks className="w-4 h-4 inline mr-1.5" />
                    Avaliação de Desempenho
                  </button>
                </div>

                {/* Tab Content: Visão Geral */}
                {activeTab === 'visao-geral' && (
                  <div className="space-y-6 motion-safe:animate-fade-in" data-testid="tab-content-visao-geral">
                    <ThemeGrid
                      themes={categories}
                      allScores={scores}
                      allItems={items}
                      onThemeClick={handleThemeClick}
                    />

                    {/* Radar Chart + KPI Side by Side */}
                    {radarData && radarData.points.length > 0 && (
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 card p-4" data-testid="radar-chart-section">
                          <RadarChartViz
                            data={radarData.points}
                            individualAverage={radarData.individualAverage}
                            teamAverage={radarData.teamAverage}
                          />
                        </div>
                        <div className="space-y-3">
                          <div className="card p-4 text-center">
                            <p className="text-xs text-brand-gray uppercase tracking-wider mb-1">Média Individual</p>
                            <p className="text-3xl font-bold text-brand-primary">{radarData.individualAverage.toFixed(2)}</p>
                          </div>
                          <div className="card p-4 text-center">
                            <p className="text-xs text-brand-gray uppercase tracking-wider mb-1">Média Equipe</p>
                            <p className="text-3xl font-bold text-brand-gray">{radarData.teamAverage.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tab Content: Avaliação de Desempenho */}
                {activeTab === 'avaliacao-desempenho' && (
                  <div className="space-y-3 motion-safe:animate-fade-in" data-testid="tab-content-avaliacao-desempenho">
                    {categories.length === 0 && (
                      <div className="text-center py-8 text-brand-gray">
                        Nenhuma categoria de avaliação configurada
                      </div>
                    )}
                    {categories.map((theme) => {
                      const themeItems = getThemeItems(theme.categoryId)
                      const themeScores = getThemeScores(theme.categoryId)
                      return (
                        <ThemeRowAccordion
                          key={theme.categoryId}
                          id={theme.categoryId}
                          theme={theme}
                          scores={themeScores}
                          items={themeItems}
                          readOnly={readOnly}
                        />
                      )
                    })}
                    {categories.length > 0 && scores.length === 0 && (
                      <div className="text-center py-8 text-brand-gray">
                        Nenhuma avaliação encontrada para este colaborador
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const CompetenciasPageProtected = withPageProtection(CompetenciasPage, PAGES.PERFORMANCE_BONIFICACAO)

export const Route = createFileRoute('/_auth/performance/bonificacao/competencias')({
  component: CompetenciasPageProtected,
  validateSearch: (search: Record<string, unknown>): CompetenciasSearch => ({
    employee: search.employee as string | undefined,
    tab: search.tab as string | undefined,
  }),
})
